/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* tslint:disable: no-duplicate-imports */
import { getTelemetryObject, IAlertProps, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { isValidColour } from '../../common/method/functions';
import { CookieComplianceAcceptButton, CookieComplianceLinks} from './components';
import { IFarmlandsCookieComplianceConfig, IFarmlandsCookieComplianceProps } from './farmlands-cookie-compliance.props.autogenerated';

export interface IAlertState {
    visible: boolean;
}

export interface ICookieComplianceViewProps extends IFarmlandsCookieComplianceProps<{}> {
    CookieComplianceBanner: IModuleProps;
    AlertProps: IAlertProps;
    Content: INodeProps;
    text?: React.ReactNode;
    links?: React.ReactNode;
    acceptButton?: React.ReactNode;
    className: string;
    onDismiss(): void;
}

/**
 *
 * CookieCompliance component
 * @extends {React.PureComponent<IFarmlandsCookieComplianceProps<IHeroConfig>>}
 */
class CookieCompliance extends React.PureComponent<IFarmlandsCookieComplianceProps<IFarmlandsCookieComplianceConfig>, IAlertState> {
    private cookieRef: React.RefObject<HTMLInputElement>;
    private telemetryContent: ITelemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
                                                                     this.props.friendlyName,
                                                                     this.props.telemetry);
    constructor(props: IFarmlandsCookieComplianceProps<IFarmlandsCookieComplianceConfig>) {
        super(props);
        this.state = {visible:false};
        this.cookieRef = React.createRef();
        this._onDismiss = this._onDismiss.bind(this);
    }

    public componentDidMount(): void {
        const { context } = this.props;

        if (context && context.request && context.request.cookies && !context.request.cookies.isConsentGiven()) {
            this.setState({visible: true});
        }
    }

    public render(): JSX.Element | null {
        const { config, context, resources, telemetry } = this.props;
        const { bannerColour, bannerButtonColour } = config;
        const { acceptCookiesButtonText, acceptCookiesAriaLabel } = resources;
        const { visible } = this.state;

        const isConsentGiven = context && context.request && context.request.cookies && context.request.cookies.isConsentGiven();
        let moduleConfig: IFarmlandsCookieComplianceConfig;

        try {
            moduleConfig = config;
        } catch(e) {
            telemetry.error(`Something went wrong while rendering the alert module ------${e}`);
            return <div>{e}</div>;
        }

        if (isConsentGiven && !visible) {
            this.props.context.telemetry.error('Cookie Compliance content is empty, module wont render.');
            return null;
        }

        const viewProps = {
            ...this.props,
            onDismiss: this._onDismiss,
            CookieComplianceBanner: {
                moduleProps: this.props,
                className: classnames('ms-cookie-compliance', moduleConfig.className),
                ref: this.cookieRef,
                style: { 'background-color': isValidColour(bannerColour) || '#00553d' }
            },
            AlertProps: {
                className: 'ms-cookie-compliance__container',
                color: 'white',
                fade: false
            },
            Content: {
                className: 'ms-cookie-compliance__content'
            },
            text: moduleConfig.content && (
                <MsDyn365.RichTextComponent
                    className='ms-cookie-compliance__text'
                    text={ moduleConfig.content }
                    editProps= {{onEdit: this.handleContentChange, requestContext: this.props.context.request}}
                />
            ),
            links: moduleConfig.actionLinks && moduleConfig.actionLinks.length  > 0 && (
                <CookieComplianceLinks
                    links={ moduleConfig.actionLinks }
                    onTextChange= {this.handleLinkTextChange}
                    requestContext= {this.props.context.request}
                    telemetryContent= {this.telemetryContent}
                />
            ),
            acceptButton: (
                <CookieComplianceAcceptButton
                    onClose={this._onDismiss}
                    acceptCookiesButtonText={acceptCookiesButtonText}
                    acceptCookiesButtonAriaLabel={acceptCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                    colour={isValidColour(bannerButtonColour) || '#f8971d'}
                />)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /* Handle link text change*/
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent) => {
        if(this.props.config.actionLinks && this.props.config.actionLinks[linkIndex]) {
        this.props.config.actionLinks[linkIndex].linkText = event.target.value;
        }
    };

    /* Handle text content change*/
    public handleContentChange = (event: MsDyn365.ContentEditableEvent) => this.props.config.content = event.target.value;

    private _onDismiss(): void {
        const { context } = this.props;

        if (context && context.request && context.request.cookies) {
            context.request.cookies.setConsentCookie();
            location.reload();
        }
        this.setState({visible: false});
    }
}

export default CookieCompliance;